import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetVacanciesQuery, useDeleteVacancyMutation } from "./api/vacanciesApi";
import { Vacancy } from "./types/vacancy";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditVacancy from "./components/AddEditVacancy";
import VacancyDetail from "./components/VacancyDetail";

const VacancyManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);
  const navigate = useNavigate();

  const { data: vacancies, isLoading, isError, isFetching } = useGetVacanciesQuery();
  const [deleteVacancy] = useDeleteVacancyMutation();

  const columns: Array<MRT_ColumnDef<Vacancy>> = [
    {
      header: "Job Title",
      accessorKey: "job_title",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.job_title}
          </Typography>
        );
      },
    },
    {
      header: "Job Type",
      accessorKey: "job_type",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.job_type} 
            color="primary" 
            size="small" 
          />
        );
      },
    },
    {
      header: "Job Site",
      accessorKey: "job_site",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.job_site} 
            color="secondary" 
            size="small" 
          />
        );
      },
    },
    {
      header: "Experience",
      accessorKey: "experience_level",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.experience_level} 
            color="info" 
            size="small" 
          />
        );
      },
    },
    {
      header: "Vacancies",
      accessorKey: "vacancies",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.vacancies.toString()} 
            color="success" 
            size="small" 
          />
        );
      },
    },
    {
      header: "Deadline",
      accessorKey: "deadline",
      size: 120,
      Cell({ row: { original } }) {
        const deadline = new Date(original.deadline);
        const isOverdue = deadline < new Date();
        return (
          <Typography 
            variant="body2" 
            color={isOverdue ? "error" : "text.primary"}
            fontWeight={isOverdue ? "bold" : "normal"}
          >
            {deadline.toLocaleDateString()}
          </Typography>
        );
      },
    },
    {
      header: "Date Posted",
      accessorKey: "date_posted",
      size: 120,
      Cell({ row: { original } }) {
        return new Date(original.date_posted).toLocaleDateString();
      },
    },
    {
      header: "Actions",
      id: "actions",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVacancy(original);
                setOpenDetailDialog(true);
              }}
              color="primary"
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVacancy(original);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVacancy(original);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedVacancy) {
      try {
        await deleteVacancy(selectedVacancy.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedVacancy(null);
      } catch (error) {
        console.error("Failed to delete vacancy:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedVacancy(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Vacancy Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedVacancy(null);
            setOpenDialog(true);
          }}
        >
          Add New Vacancy
        </Button>
      </Box>

      <DataTable
        columns={columns}
        list={vacancies?.data || []}
        count={vacancies?.data?.length || 0}
        getRowId={(row) => row?.id?.toString() || Math.random().toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        pagination={pagination}
        onPaginationChange={setPagination}
        enableRowSelection={false}
        onRowClick={(row) => {
          setSelectedVacancy(row);
          setOpenDetailDialog(true);
        }}
      />

      <AddEditVacancy
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        vacancy={selectedVacancy}
      />

      <VacancyDetail
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        vacancy={selectedVacancy}
      />

      <ConfirmModal
        modalOpen={openConfirmModal}
        setModalOpen={setOpenConfirmModal}
        onOk={handleDelete}
        title="Delete Vacancy"
        message={`Are you sure you want to delete "${selectedVacancy?.job_title || 'this vacancy'}"? This action cannot be undone.`}
        type="warning"
      />
    </Box>
  );
};

export default VacancyManager;

